import React, { useState, useEffect, useMemo } from "react";
import MetaTags from "react-meta-tags";
import { Container, Modal, Row, Col, Button, Badge } from "reactstrap";
import { get, post, put, del } from "helpers/api_helper";
import { APPOINTMENT_URL, PATIENT_URL, CHAIR_URL, DOCTOR_TIMING_URL } from "helpers/url_helper";
import { showErroeAlert, showSuccessAlert, showConfirmAlert, showDeleteConfirmationWithText } from "pages/utils/alertMessages";
import Select from "react-select";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LiveClock from "./utils/liveClock";
import cookieHelper from "helpers/getCookieData";
import { encodePatientId } from "helpers/url_crypto_helper";
import { getLoggedInDoctorOption, getLoggedInDoctorCode, getUserRole } from "helpers/permissionHelper";
import { AvForm, AvField } from "availity-reactstrap-validation";
import {
    RiCalendarEventLine,
    RiTimeLine,
    RiUserHeartLine,
    RiUserAddLine,
    RiUserReceivedLine,
    RiHeartPulseLine,
    RiStethoscopeLine,
    RiHospitalLine,
    RiSettings4Line,
    RiCheckLine,
    RiCloseLine,
    RiEyeLine,
    RiWhatsappLine,
    RiEditLine,
    RiAddCircleLine,
    RiRefreshLine,
    RiArrowLeftSLine,
    RiArrowRightSLine,
    RiMedicineBottleLine,
    RiBookmarkLine,
    RiTimeFill
} from "react-icons/ri";
import "./chairs.scss";

const timeSlotsList = [
    "08:00:00",
    "09:00:00",
    "10:00:00",
    "11:00:00",
    "12:00:00",
    "13:00:00",
    "14:00:00",
    "15:00:00",
    "16:00:00",
    "17:00:00",
    "18:00:00",
    "19:00:00",
    "20:00:00",
    "21:00:00"
];

const toSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m, s] = timeStr.split(":").map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};

const timesOptions = [
    { label: "07:00 AM", value: "07:00:00" },
    { label: "07:30 AM", value: "07:30:00" },
    { label: "08:00 AM", value: "08:00:00" },
    { label: "08:30 AM", value: "08:30:00" },
    { label: "09:00 AM", value: "09:00:00" },
    { label: "09:30 AM", value: "09:30:00" },
    { label: "10:00 AM", value: "10:00:00" },
    { label: "10:30 AM", value: "10:30:00" },
    { label: "11:00 AM", value: "11:00:00" },
    { label: "11:30 AM", value: "11:30:00" },
    { label: "12:00 PM", value: "12:00:00" },
    { label: "12:30 PM", value: "12:30:00" },
    { label: "01:00 PM", value: "13:00:00" },
    { label: "01:30 PM", value: "13:30:00" },
    { label: "02:00 PM", value: "14:00:00" },
    { label: "02:30 PM", value: "14:30:00" },
    { label: "03:00 PM", value: "15:00:00" },
    { label: "03:30 PM", value: "15:30:00" },
    { label: "04:00 PM", value: "16:00:00" },
    { label: "04:30 PM", value: "16:30:00" },
    { label: "05:00 PM", value: "17:00:00" },
    { label: "05:30 PM", value: "17:30:00" },
    { label: "06:00 PM", value: "18:00:00" },
    { label: "06:30 PM", value: "18:30:00" },
    { label: "07:00 PM", value: "19:00:00" },
    { label: "07:30 PM", value: "19:30:00" },
    { label: "08:00 PM", value: "20:00:00" },
    { label: "08:30 PM", value: "20:30:00" },
    { label: "09:00 PM", value: "21:00:00" },
    { label: "09:30 PM", value: "21:30:00" },
    { label: "10:00 PM", value: "22:00:00" }
];

const WelcomeChairScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const isMountedRef = React.useRef(true);
    const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;

    const [chairsList, setChairsList] = useState([]);
    const isDoctor = getUserRole() === 'doctor';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isChairModalOpen, setIsChairModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [DDoptions, setDDOptions] = useState({ patients: [], doctors: [], treatments: [] });
    const [appointment, setAppointment] = useState({});
    const [todoListData, setTodoListData] = useState([]);
    const [doctorAllocations, setDoctorAllocations] = useState([]);
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [hidePastSlots, setHidePastSlots] = useState(true);
    const [newChairData, setNewChairData] = useState({
        title: '',
        cabin_no: 1,
        start_time: '08:00:00',
        end_time: '13:00:00',
        intervel: 30,
        description: ''
    });

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Fetch Operatory Chairs
    const fetchChairs = async () => {
        setLoading(true);
        const clinicParam = clinic?.id ? `?clinic_id=${clinic.id}&limit=all` : '?limit=all';
        try {
            const { success, body } = await get(`${CHAIR_URL}${clinicParam}`);
            if (isMountedRef.current && success) {
                setChairsList(body?.items || []);
            }
        } catch (e) {
            // ignore
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    };

    // Fetch Doctor Chair Allocations
    const fetchDoctorAllocations = async () => {
        try {
            const res = await get(`${DOCTOR_TIMING_URL}?limit=all`);
            if (isMountedRef.current && (res?.success || res?.body || res?.items)) {
                const list = res?.body?.items || res?.items || res?.body || [];
                setDoctorAllocations(list);
            }
        } catch (e) {
            // ignore
        }
    };

    // Fetch Appointments for Selected Date
    const getTodoListData = async (date = selectedDate) => {
        try {
            const clinicParam = clinic?.id ? `&clinic_id=${clinic.id}` : '';
            const { success, body } = await get(`${APPOINTMENT_URL}/todo?flag=c&_date=${date}${clinicParam}`);
            if (isMountedRef.current && success) {
                setTodoListData(body || []);
            }
        } catch (e) {
            // ignore
        }
    };

    // Fetch Dropdown options (Patients, Doctors, Treatments)
    const fetchOptions = async () => {
        try {
            const { success, body } = await get(`${PATIENT_URL}/options`);
            if (isMountedRef.current && success) {
                setDDOptions(body || {});
            }
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    };

    useEffect(() => {
        fetchChairs();
        fetchOptions();
        fetchDoctorAllocations();
    }, []);

    useEffect(() => {
        getTodoListData(selectedDate);
    }, [selectedDate]);

    // Map each distinct Operatory Chair with its active doctor allocations
    const displayChairs = useMemo(() => {
        if (!chairsList || chairsList.length === 0) return [];
        return chairsList.map(chair => {
            const allocations = (doctorAllocations || []).filter(a => {
                if (Number(a.status) !== 0) return false;
                return String(a.chair_code) === String(chair.id);
            });

            return {
                ...chair,
                allocations
            };
        }).sort((a, b) => (Number(a.cabin_no) || 0) - (Number(b.cabin_no) || 0) || (Number(a.id) || 0) - (Number(b.id) || 0));
    }, [chairsList, doctorAllocations]);

    const toggleAppointmentModal = () => setIsModalOpen(!isModalOpen);
    const toggleChairModal = () => {
        setIsChairModalOpen(!isChairModalOpen);
        setNewChairData({
            title: '',
            cabin_no: (displayChairs.length || 0) + 1,
            start_time: '08:00:00',
            end_time: '13:00:00',
            intervel: 30,
            description: ''
        });
    };

    const handleCreateChair = async () => {
        try {
            const payload = {
                ...newChairData,
                clinic_id: clinic?.id,
                cabin_no: parseInt(newChairData.cabin_no, 10) || 1,
                intervel: parseInt(newChairData.intervel, 10) || 30,
            };
            const res = await post(CHAIR_URL, payload);
            if (res?.success || res?.id) {
                showSuccessAlert("Dental operatory shift/chair added successfully!");
                setIsChairModalOpen(false);
                fetchChairs();
            } else {
                showErroeAlert(res?.message || "Failed to create chair. Please check your clinic plan or details.");
            }
        } catch (err) {
            const errMsg = err?.response?.data?.message || err?.message || "Error occurred while creating chair.";
            showErroeAlert(errMsg);
        }
    };

    const handleAppointmentSubmit = async () => {
        delete appointment?.chairdata;

        const effectiveDoctorCode = appointment?.doctor_code || (!appointment?.id ? getLoggedInDoctorCode(DDoptions?.doctors || []) : '');

        if (!appointment?.patient_id || !effectiveDoctorCode) {
            showErroeAlert('Please select both Patient and Attending Doctor!');
            return;
        }

        // Prevent booking new appointments in past timeslots
        if (!appointment?.id && moment(appointment?.appointment_date).add(30, 'minutes').isBefore(moment())) {
            showErroeAlert('Cannot book an appointment for a past time slot! Please select an upcoming slot or future date.');
            return;
        }

        const payload = {
            ...appointment,
            doctor_code: effectiveDoctorCode,
            clinic_id: clinic?.id
        };

        const res = appointment?.id
            ? await put(`${APPOINTMENT_URL}/${appointment.id}`, payload)
            : await post(APPOINTMENT_URL, payload);

        if (res?.success) {
            showSuccessAlert(appointment?.id ? 'Appointment updated successfully!' : 'Appointment booked successfully!');
            setIsModalOpen(false);
            setAppointment({});
            getTodoListData(selectedDate);
        } else {
            showErroeAlert('Unable to save appointment. Please check details.');
        }
    };

    const handleModalForBookSlot = (chair, hour, indexPosition, allocatedDocCode) => {
        const slotDateTime = `${selectedDate} ${hour.substring(0, 5)}`;

        if (moment(slotDateTime, 'YYYY-MM-DD HH:mm').add(1, 'hour').isBefore(moment())) {
            showErroeAlert('Cannot book a past time slot. Please choose an upcoming time slot or future date.');
            return;
        }

        // Match doctor in options or fallback to logged in doctor
        let matchedDoc = (DDoptions?.doctors || []).find(d =>
            (allocatedDocCode && String(d.value).toLowerCase() === String(allocatedDocCode).toLowerCase()) ||
            (allocatedDocCode && String(d.label).toLowerCase() === String(allocatedDocCode).toLowerCase())
        );

        if (!matchedDoc && !allocatedDocCode) {
            matchedDoc = getLoggedInDoctorOption(DDoptions?.doctors || []);
        }

        const defaultDoctorCode = matchedDoc?.value || allocatedDocCode || getLoggedInDoctorCode(DDoptions?.doctors || []) || '';

        setAppointment({
            appointment_date: slotDateTime,
            chairdata: chair || { title: `Operatory Chair #${chair?.id} (Cabin ${chair?.cabin_no})` },
            chair_code: chair?.id,
            index_position: indexPosition,
            patient_id: '',
            doctor_code: defaultDoctorCode,
            treatment_code: '',
            notes: ''
        });
        setIsModalOpen(true);
    };

    const handleModifyBooking = (bookingData, chair) => {
        const date = moment(bookingData?.appointment_date || bookingData?.start).format('YYYY-MM-DD');
        const hour = moment(bookingData?.appointment_date || bookingData?.start).format('HH:mm');

        setAppointment({
            id: bookingData?.id,
            chairdata: chair || { title: `Operatory Chair #${chair?.id} (Cabin ${chair?.cabin_no})` },
            treatment_code: bookingData?.tretment_code,
            notes: bookingData?.notes,
            patient_id: bookingData?.patient_id,
            appointment_date: `${date} ${hour}`,
            chair_code: bookingData?.chair_code || chair?.id,
            index_position: bookingData?.index_position,
            doctor_code: bookingData?.doctor?.code || bookingData?.doctor_code
        });
        setIsModalOpen(true);
    };

    const handleCancelBooking = (id) => {
        showDeleteConfirmationWithText(async (note) => {
            await del(`${APPOINTMENT_URL}/${id}?note=${encodeURIComponent(note || 'Cancelled by clinic admin')}`);
            showSuccessAlert("Appointment cancelled successfully.");
            getTodoListData(selectedDate);
        }, "Reason for canceling this appointment");
    };

    const handlePatientIn = async (item) => {
        try {
            const nowIso = new Date().toISOString();
            const res = await put(`${APPOINTMENT_URL}/${item.id}`, {
                arravel_time: nowIso,
                is_visited: 3
            });
            if (res?.success) {
                showSuccessAlert(`Patient ${item.patient?.first_name || ''} marked as Arrived (In)!`);
                getTodoListData(selectedDate);
            } else {
                showErroeAlert("Failed to mark patient arrival.");
            }
        } catch (e) {
            showErroeAlert("Failed to mark patient arrival.");
        }
    };

    const handleStartTreatment = async (item) => {
        try {
            const nowIso = new Date().toISOString();
            const res = await put(`${APPOINTMENT_URL}/${item.id}`, {
                attened_time: nowIso,
                is_visited: 1
            });
            if (res?.success) {
                showSuccessAlert(`Treatment Started for ${item.patient?.first_name || ''}!`);
                getTodoListData(selectedDate);
                history.push(`/patient?_p=${encodePatientId(item.patient_id)}&_t=Treatment&_appt=${item.id}`);
            } else {
                showErroeAlert("Failed to start treatment.");
            }
        } catch (e) {
            showErroeAlert("Failed to start treatment.");
        }
    };

    // Calculate metrics
    const bookedCount = (todoListData || []).filter(v => v.canceled_at === 'Invalid date' || !v.canceled_at).length;

    const isPastDate = moment(selectedDate).isBefore(moment(), 'day');
    const effectiveHidePast = hidePastSlots && !isPastDate;

    // Filter time slots: when effectiveHidePast is true, show past slots with bookings, hide unbooked past slots, and show all upcoming/available slots
    const visibleTimeSlots = useMemo(() => {
        if (!effectiveHidePast) return timeSlotsList;

        return timeSlotsList.filter(hour => {
            const slotStartMoment = moment(`${selectedDate} ${hour}`, 'YYYY-MM-DD HH:mm:ss');
            const slotEndMoment = slotStartMoment.clone().add(1, 'hour');
            const isPastSlot = slotEndMoment.isBefore(moment());

            // If slot is current or upcoming, show it
            if (!isPastSlot) return true;

            // If slot is in the past: show if any non-canceled booking exists in this time slot, otherwise hide
            const hasBooking = (todoListData || []).some(v => {
                if (v.canceled_at && v.canceled_at !== 'Invalid date') return false;
                return moment(v.appointment_date).format('HH') === hour.substring(0, 2);
            });

            return hasBooking;
        });
    }, [effectiveHidePast, selectedDate, todoListData]);

    // Count of currently bookable slots for the selected date
    const availableSlotsCount = useMemo(() => {
        let count = 0;
        (timeSlotsList || []).forEach(hour => {
            const slotStartSec = toSeconds(hour);
            const slotEndSec = slotStartSec + 3600;
            const slotStartMoment = moment(`${selectedDate} ${hour}`, 'YYYY-MM-DD HH:mm:ss');
            const slotEndMoment = slotStartMoment.clone().add(1, 'hour');
            const isPastSlot = slotEndMoment.isBefore(moment());

            if (isPastSlot) return; // Cannot be booked

            (displayChairs || []).forEach(chair => {
                const chairStartSec = toSeconds(chair.start_time || "08:00:00");
                const chairEndSec = toSeconds(chair.end_time || "21:00:00");
                const isCovered = (chairStartSec < chairEndSec)
                    ? (slotStartSec < chairEndSec && slotEndSec > chairStartSec)
                    : (slotStartSec < 86400 && slotEndSec > chairStartSec) || (slotStartSec < chairEndSec);

                if (!isCovered) return;

                const isBooked = (todoListData || []).some(v => {
                    if (v.canceled_at && v.canceled_at !== 'Invalid date') return false;
                    return String(v.chair_code) === String(chair.id) &&
                        moment(v.appointment_date).format('HH') === hour.substring(0, 2);
                });

                if (!isBooked) {
                    count++;
                }
            });
        });
        return count;
    }, [selectedDate, displayChairs, todoListData]);

    const pastSlotsCount = Math.max(0, timeSlotsList.length - visibleTimeSlots.length);

    return (
        <React.Fragment>
            <div className="page-content chairs-page-container">
                <MetaTags>
                    <title>{t("Chairs")} | 909 Dental OPD</title>
                </MetaTags>

                <Container fluid className="px-lg-4">
                    {/* Header Controls Bar */}
                    <div className="chairs-top-header">
                        <Row className="align-items-center g-3">
                            <Col xl={5} lg={5} md={12}>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="chairs-brand-badge">
                                        <RiHospitalLine />
                                    </div>
                                    <div>
                                        <h4 className="chairs-header-title">{t("Operatory Chairs & Live OPD Flow")}</h4>
                                        <p className="chairs-header-sub">
                                            {moment(selectedDate).format("dddd, DD MMMM YYYY")}
                                        </p>
                                    </div>
                                </div>
                            </Col>

                            <Col xl={7} lg={7} md={12} className="text-lg-end">
                                <div className="d-flex align-items-center justify-content-lg-end gap-2 flex-wrap">
                                    <div className="chairs-clock-widget">
                                        <RiTimeLine />
                                        <LiveClock />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                                        onClick={() => { fetchChairs(); getTodoListData(selectedDate); fetchDoctorAllocations(); }}
                                        title="Refresh Schedule"
                                    >
                                        <RiRefreshLine />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 font-weight-semibold"
                                        onClick={toggleChairModal}
                                    >
                                        <RiAddCircleLine />{t("Chairs")}
                                    </button>
                                    <Link to="/patients" className="btn btn-success btn-sm d-inline-flex align-items-center gap-1 font-weight-semibold">
                                        <RiUserAddLine /> {t("Add New Patient")}
                                    </Link>
                                    <Link to="/settings?_s=chair" className="btn btn-light btn-sm border text-muted" title="Manage All Chairs in Settings">
                                        <RiSettings4Line />
                                    </Link>
                                </div>
                            </Col>
                        </Row>

                        {/* Navigation & Metrics Row */}
                        <div className="d-flex align-items-center justify-content-between gap-3 mt-3 pt-3 border-top flex-wrap">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div className="chairs-date-nav">
                                    <button
                                        type="button"
                                        className="chairs-date-btn"
                                        onClick={() => setSelectedDate(moment(selectedDate).subtract(1, 'days').format('YYYY-MM-DD'))}
                                        title="Previous Day"
                                    >
                                        <RiArrowLeftSLine />
                                    </button>
                                    <div className="chairs-date-input-wrap">
                                        <RiCalendarEventLine className="text-primary font-size-14" />
                                        <input
                                            type="date"
                                            className="chairs-date-input"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="chairs-date-btn"
                                        onClick={() => setSelectedDate(moment(selectedDate).add(1, 'days').format('YYYY-MM-DD'))}
                                        title="Next Day"
                                    >
                                        <RiArrowRightSLine />
                                    </button>
                                    <button
                                        type="button"
                                        className="chairs-today-btn"
                                        onClick={() => setSelectedDate(moment().format('YYYY-MM-DD'))}
                                    >
                                        {t("Today") || "Today"}
                                    </button>
                                </div>

                                <div className="chairs-stat-pill">
                                    <span>{t("Operatory Chairs:") || "Operatory Chairs:"}</span>
                                    <strong>{displayChairs.length}</strong>
                                </div>
                                <div className="chairs-stat-pill stat-available">
                                    <span>{t("Available Slots:") || "Available Slots:"}</span>
                                    <strong className="text-success">{availableSlotsCount}</strong>
                                </div>
                                <div className="chairs-stat-pill">
                                    <span>{t("Appointments Today:") || "Appointments Today:"}</span>
                                    <strong className="text-primary">{bookedCount}</strong>
                                </div>
                                <div className="chairs-stat-pill">
                                    <span>{t("Interval Slot:") || "Interval Slot:"}</span>
                                    <strong>30 {t("Mins") || "Mins"}</strong>
                                </div>

                                {/* Filter Toggle: Available Only vs All Slots */}
                                <div className="chairs-filter-nav">
                                    <button
                                        type="button"
                                        className={`chairs-filter-pill-btn ${effectiveHidePast ? 'active' : ''}`}
                                        onClick={() => setHidePastSlots(true)}
                                        title={t("Hide empty past slots and show only available slots & bookings")}
                                    >
                                        <RiCheckLine className="me-1 font-size-14" />
                                        {t("Available & Bookings")}
                                        {effectiveHidePast && pastSlotsCount > 0 && (
                                            <span className="badge bg-white text-primary ms-1.5 px-1.5 py-0.5 font-size-10 font-weight-bold">
                                                {pastSlotsCount} {t("Empty Past Hidden")}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className={`chairs-filter-pill-btn ${!effectiveHidePast ? 'active' : ''}`}
                                        onClick={() => setHidePastSlots(false)}
                                        title={t("Show full day timeline including past time slots")}
                                    >
                                        <RiTimeLine className="me-1 font-size-14" />
                                        {t("All Slots")}
                                    </button>
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-3 font-size-12 text-muted flex-wrap">
                                <span className="d-inline-flex align-items-center gap-1">
                                    <span className="d-inline-block rounded-circle bg-success" style={{ width: 8, height: 8 }} /> {t("Available Slot") || "Available Slot"}
                                </span>
                                <span className="d-inline-flex align-items-center gap-1">
                                    <span className="d-inline-block rounded-circle bg-primary" style={{ width: 8, height: 8 }} /> {t("Booked Slot") || "Booked Slot"}
                                </span>
                                <span className="d-inline-flex align-items-center gap-1">
                                    <span className="d-inline-block rounded-circle bg-secondary" style={{ width: 8, height: 8 }} /> {t("Off Hours / Break") || "Off Hours / Break"}
                                </span>
                                {!effectiveHidePast && (
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <span className="d-inline-block rounded-circle bg-dark opacity-50" style={{ width: 8, height: 8 }} /> {t("Past Slot") || "Past Slot"}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Empty State when no chairs exist */}
                    {displayChairs.length === 0 && !loading && (
                        <div className="chairs-empty-box">
                            <div className="chairs-empty-icon-wrap">
                                <RiHospitalLine />
                            </div>
                            <h4 className="font-weight-bold text-slate-800 mb-2">{t("No Dental Operatory Chairs Configured") || "No Dental Operatory Chairs Configured"}</h4>
                            <p className="text-muted font-size-14 mb-4" style={{ maxWidth: '460px', margin: '0 auto 1.5rem' }}>
                                {t("Add your clinic operatory cabins and operating time shifts to start managing chair allocations.") || "Add your clinic operatory cabins and operating time shifts (e.g. Morning 08:00-13:00, Evening 15:00-20:00) to start managing chair allocations."}
                            </p>
                            <div className="d-flex align-items-center justify-content-center gap-3">
                                <button type="button" className="btn btn-primary px-4" onClick={toggleChairModal}>
                                    <RiAddCircleLine className="me-1" /> {t("Add New") || "Add Dental Chair"}
                                </button>
                                <Link to="/settings?_s=chair" className="btn btn-outline-secondary px-3">
                                    <RiSettings4Line className="me-1" /> {t("Settings") || "Open Chairs Settings"}
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Operatory Chairs Calendar Matrix Table */}
                    {displayChairs.length > 0 && (
                        <div className="chairs-matrix-card">
                            <div className="chairs-matrix-table-wrap">
                                <table className="chairs-matrix-table">
                                    <thead>
                                        <tr>
                                            <th className="matrix-time-col-header">
                                                <RiTimeLine className="me-1 font-size-14" /> {t("TIME")}
                                            </th>
                                            {displayChairs.map((chair, colIndex) => (
                                                <th key={`chair-head-${chair.id || colIndex}`} className="matrix-chair-col-header">
                                                    <div className="chair-header-content">
                                                        <div className="chair-header-title-box">
                                                            <div className="chair-header-icon">
                                                                <RiHospitalLine />
                                                            </div>
                                                            <div>
                                                                <div className="d-flex align-items-center gap-1.5 flex-wrap">
                                                                    <h6 className="chair-header-title mb-0">{chair.title || `Operatory #${chair.id}`}</h6>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-1.5 flex-wrap mt-1">
                                                                    <span className="chair-shift-time-pill" title={t("Operating Hours")}>
                                                                        <RiTimeFill className="font-size-10 me-1" />
                                                                        {chair.start_time?.substring(0, 5)} - {chair.end_time?.substring(0, 5)}
                                                                    </span>
                                                                </div>
                                                                {/* Assigned Doctors on this Chair */}
                                                                <div className="d-flex align-items-center gap-1 flex-wrap mt-1.5">
                                                                    {chair.allocations.length === 0 ? (
                                                                        <span className="badge bg-light text-muted border font-size-10 font-weight-normal py-0.5 px-1.5">
                                                                            {t("Open Operatory (Any Doctor)")}
                                                                        </span>
                                                                    ) : (
                                                                        chair.allocations.map((alloc, aIdx) => {
                                                                            const docName = alloc.doctor?.name || alloc.doctor_code;
                                                                            const docColor = alloc.doctor?.color_code || '#F8CA15';
                                                                            const start = alloc.start_time?.substring(0, 5);
                                                                            const end = alloc.end_time?.substring(0, 5);
                                                                            return (
                                                                                <span
                                                                                    key={`chair-doc-${alloc.id || aIdx}`}
                                                                                    className="badge d-inline-flex align-items-center gap-1 font-size-10 font-weight-semibold shadow-2xs py-0.5 px-1.5"
                                                                                    style={{
                                                                                        backgroundColor: '#ffffff',
                                                                                        color: '#0f172a',
                                                                                        border: `1px solid ${docColor}`,
                                                                                        borderLeft: `3px solid ${docColor}`
                                                                                    }}
                                                                                    title={`${docName} (${start} - ${end})`}
                                                                                >
                                                                                    <RiStethoscopeLine style={{ color: docColor }} />
                                                                                    <span>{docName}</span>
                                                                                    <span className="text-muted font-monospace font-size-9">({start} - {end})</span>
                                                                                </span>
                                                                            );
                                                                        })
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span className="chair-header-cabin-badge flex-shrink-0">
                                                            {t("Cabin")} {chair.cabin_no || 1}
                                                        </span>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleTimeSlots.length === 0 ? (
                                            <tr>
                                                <td colSpan={displayChairs.length + 1} className="p-4 text-center">
                                                    <div className="py-4 text-center">
                                                        <RiTimeLine className="font-size-32 text-muted mb-2 d-block mx-auto" />
                                                        <h6 className="font-weight-bold text-slate-800 mb-1">{t("No Remaining Upcoming Slots For Today")}</h6>
                                                        <p className="text-muted font-size-13 mb-3">
                                                            {t("All scheduled time slots for today have completed. Click below to show earlier slots or select another date.")}
                                                        </p>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary btn-sm"
                                                            onClick={() => setHidePastSlots(false)}
                                                        >
                                                            <RiTimeLine className="me-1" /> {t("Show All Slots (Including Past)")}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            visibleTimeSlots.map((hour, rowIndex) => {
                                            const slotStartSec = toSeconds(hour);
                                            const slotEndSec = slotStartSec + 3600;
                                            const formattedHour = moment(hour, 'HH:mm:ss').format('hh:mm A');
                                            const formattedHourEnd = moment(hour, 'HH:mm:ss').add(1, 'hour').format('hh:mm A');

                                            return (
                                                <tr key={`matrix-row-${rowIndex}`}>
                                                    <td className="matrix-time-cell">
                                                        <div>{formattedHour}</div>
                                                        <span className="font-size-11 text-muted">{formattedHourEnd}</span>
                                                    </td>

                                                    {displayChairs.map((chair, colIndex) => {
                                                        const indexKey = `${rowIndex}-${colIndex}`;

                                                        // Check if chair is operating during this hour
                                                        const chairStartSec = toSeconds(chair.start_time || "08:00:00");
                                                        const chairEndSec = toSeconds(chair.end_time || "21:00:00");
                                                        const isCovered = (chairStartSec < chairEndSec)
                                                            ? (slotStartSec < chairEndSec && slotEndSec > chairStartSec)
                                                            : (slotStartSec < 86400 && slotEndSec > chairStartSec) || (slotStartSec < chairEndSec);

                                                        // Find booking for this chair at this slot time
                                                        const bookedItem = (todoListData || []).find(v => {
                                                            if (v.canceled_at && v.canceled_at !== 'Invalid date') return false;
                                                            const isMatchingChair = String(v.chair_code) === String(chair.id);
                                                            const isMatchingHour = moment(v.appointment_date).format('HH') === hour.substring(0, 2);
                                                            return isMatchingChair && isMatchingHour;
                                                        });

                                                        // Find allocated on-duty doctor for this chair & hour
                                                        const allocatedItem = chair.allocations.find(a => {
                                                            const aStart = toSeconds(a.start_time);
                                                            const aEnd = toSeconds(a.end_time);
                                                            return slotStartSec < aEnd && slotEndSec > aStart;
                                                        });

                                                        const allocatedDocObj = (DDoptions?.doctors || []).find(d =>
                                                            (allocatedItem?.doctor_code && String(d.value).toLowerCase() === String(allocatedItem.doctor_code).toLowerCase()) ||
                                                            (allocatedItem?.doctor?.code && String(d.value).toLowerCase() === String(allocatedItem.doctor.code).toLowerCase()) ||
                                                            (allocatedItem?.doctor?.name && String(d.label).toLowerCase() === String(allocatedItem.doctor.name).toLowerCase())
                                                        );
                                                        const allocatedDocName = allocatedItem?.doctor?.name || allocatedDocObj?.label || null;
                                                        const allocatedDocCode = allocatedDocObj?.value || allocatedItem?.doctor?.code || allocatedItem?.doctor_code || null;
                                                        const docColor = allocatedItem?.doctor?.color_code || allocatedDocObj?.color_code || '#F8CA15';
                                                        const docTimingWindow = allocatedItem ? `${allocatedItem.start_time?.substring(0, 5)} - ${allocatedItem.end_time?.substring(0, 5)}` : null;

                                                        if (!isCovered) {
                                                            return (
                                                                <td key={`slot-${indexKey}`} className="matrix-slot-cell">
                                                                    <div className="slot-off-hours-box">
                                                                        <span className="slot-off-hours-text">{t("Off Hours / Break")}</span>
                                                                    </div>
                                                                </td>
                                                            );
                                                        }

                                                        if (bookedItem) {
                                                            const patientName = `${bookedItem.patient?.first_name || ''} ${bookedItem.patient?.last_name || ''}`.trim() || 'Patient';
                                                            const doctorObj = (DDoptions?.doctors || []).find(d => d.value == bookedItem.doctor_code || d.value == bookedItem.doctor?.code);
                                                            const doctorName = doctorObj?.label || bookedItem.doctor?.name || 'Doctor';
                                                            const treatmentObj = (DDoptions?.treatments || []).find(t => t.value == bookedItem.tretment_code);
                                                            const treatmentName = treatmentObj?.label || bookedItem.tretment_code;

                                                            const isCompleted = Boolean(bookedItem.completed_time);
                                                            const isInTreatment = Boolean(bookedItem.attened_time && !bookedItem.completed_time);
                                                            const isCheckedIn = Boolean(bookedItem.arravel_time && !bookedItem.attened_time);

                                                            return (
                                                                <td key={`slot-${indexKey}`} className="matrix-slot-cell">
                                                                    <div className={`slot-booked-card ${isInTreatment ? 'border-primary shadow-sm' : isCompleted ? 'border-success' : ''}`}>
                                                                        <div className="slot-booked-header">
                                                                            <Link
                                                                                to={`/patient?_p=${encodePatientId(bookedItem.patient_id)}`}
                                                                                className="slot-booked-patient"
                                                                                title={t("Open Patient EMR Chart")}
                                                                            >
                                                                                <RiUserHeartLine /> {patientName}
                                                                            </Link>
                                                                            {isCompleted ? (
                                                                                <span className="badge bg-success font-size-11 px-2 py-0.5">{t("Completed")}</span>
                                                                            ) : isInTreatment ? (
                                                                                <span className="badge bg-danger font-size-11 px-2 py-0.5 d-inline-flex align-items-center gap-1">
                                                                                    <span className="spinner-grow spinner-grow-sm" style={{ width: '6px', height: '6px' }} /> {t("In Treatment")}
                                                                                </span>
                                                                            ) : isCheckedIn ? (
                                                                                <span className="badge bg-info font-size-11 px-2 py-0.5">{t("Checked In")}</span>
                                                                            ) : (
                                                                                <span className="badge bg-warning text-dark font-size-11 px-2 py-0.5">{t("Booked")}</span>
                                                                            )}
                                                                        </div>

                                                                        <div className="slot-booked-meta">
                                                                            <div className="slot-meta-item text-truncate">
                                                                                <RiStethoscopeLine className="text-primary me-1" />
                                                                                <span className="font-weight-semibold text-slate-800">{doctorName}</span>
                                                                            </div>
                                                                            {treatmentName && (
                                                                                <div className="slot-meta-item text-truncate">
                                                                                    <RiMedicineBottleLine className="text-muted me-1" />
                                                                                    <span>{treatmentName}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Action Row */}
                                                                        {!isCheckedIn && !isInTreatment && !isCompleted && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-info font-size-11 py-1 px-2 w-100 d-flex align-items-center justify-content-center gap-1 mt-1 font-weight-semibold"
                                                                                onClick={() => handlePatientIn(bookedItem)}
                                                                                title={t("Mark Patient as Arrived at Clinic")}
                                                                            >
                                                                                <RiUserReceivedLine /> {t("Patient In (Arrived)")}
                                                                            </button>
                                                                        )}

                                                                        {isCheckedIn && (
                                                                            <>
                                                                                <div className="text-muted font-size-11 mt-1 d-flex align-items-center justify-content-between">
                                                                                    <span>{t("In Time")}:</span>
                                                                                    <strong className="text-info">{moment(bookedItem.arravel_time).format('hh:mm A')}</strong>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-primary font-size-11 py-1 px-2 w-100 d-flex align-items-center justify-content-center gap-1 mt-1 shadow-sm font-weight-semibold"
                                                                                    onClick={() => handleStartTreatment(bookedItem)}
                                                                                    title={t("Seat patient on chair & start treatment")}
                                                                                >
                                                                                    <RiHeartPulseLine /> {t("Start Treatment")}
                                                                                </button>
                                                                            </>
                                                                        )}

                                                                        {isInTreatment && (
                                                                            <>
                                                                                <div className="text-muted font-size-11 mt-1 d-flex align-items-center justify-content-between">
                                                                                    <span>{t("Started")}:</span>
                                                                                    <strong className="text-danger">{moment(bookedItem.attened_time).format('hh:mm A')}</strong>
                                                                                </div>
                                                                                <Link
                                                                                    to={`/patient?_p=${encodePatientId(bookedItem.patient_id)}&_t=Treatment&_appt=${bookedItem.id}`}
                                                                                    className="btn btn-sm btn-outline-primary font-size-11 py-1 px-2 w-100 d-flex align-items-center justify-content-center gap-1 mt-1 font-weight-semibold"
                                                                                >
                                                                                    <RiTimeLine /> {t("Open EMR / End")}
                                                                                </Link>
                                                                            </>
                                                                        )}

                                                                        {isCompleted && (
                                                                            <div className="text-muted font-size-11 mt-1 d-flex align-items-center justify-content-between">
                                                                                <span>{t("Completed")}:</span>
                                                                                <strong className="text-success">{moment(bookedItem.completed_time).format('hh:mm A')}</strong>
                                                                            </div>
                                                                        )}

                                                                        <div className="slot-booked-actions mt-1">
                                                                            <button
                                                                                type="button"
                                                                                className="slot-action-btn edit"
                                                                                title={t("Reschedule / Edit Appointment")}
                                                                                onClick={() => handleModifyBooking(bookedItem, chair)}
                                                                            >
                                                                                <RiEditLine />
                                                                            </button>
                                                                            <Link
                                                                                to={`/patient?_p=${encodePatientId(bookedItem.patient_id)}`}
                                                                                className="slot-action-btn view"
                                                                                title={t("View Patient Chart / EMR")}
                                                                            >
                                                                                <RiEyeLine />
                                                                            </Link>
                                                                            {bookedItem.patient?.mobile && (
                                                                                <a
                                                                                    href={`https://wa.me/${bookedItem.patient.mobile.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${patientName}, your appointment at 909 Dental OPD is scheduled on ${moment(bookedItem.appointment_date).format('DD MMM YYYY, hh:mm A')}.`)}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="slot-action-btn whatsapp"
                                                                                    title={t("Send WhatsApp Confirmation")}
                                                                                >
                                                                                    <RiWhatsappLine />
                                                                                </a>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className="slot-action-btn cancel ms-auto"
                                                                                title={t("Cancel Appointment")}
                                                                                onClick={() => handleCancelBooking(bookedItem.id)}
                                                                            >
                                                                                <RiCloseLine />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            );
                                                        }

                                                        const slotStartMoment = moment(`${selectedDate} ${hour}`, 'YYYY-MM-DD HH:mm:ss');
                                                        const slotEndMoment = slotStartMoment.clone().add(1, 'hour');
                                                        const isPastSlot = slotEndMoment.isBefore(moment());

                                                        // If time slot is in the past and unbooked → disable booking
                                                        if (isPastSlot) {
                                                            return (
                                                                <td key={`slot-${indexKey}`} className="matrix-slot-cell">
                                                                    <div className="slot-past-box" title={t("Past time slot cannot be booked")}>
                                                                        <span className="slot-past-text">
                                                                            <RiTimeLine /> {t("Past Slot")}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            );
                                                        }

                                                        // Available Slot for booking
                                                        return (
                                                            <td key={`slot-${indexKey}`} className="matrix-slot-cell">
                                                                <div
                                                                    className={`slot-available-box flex-column align-items-stretch ${allocatedDocName ? 'has-assigned-doctor' : ''}`}
                                                                    onClick={() => handleModalForBookSlot(chair, hour, indexKey, allocatedDocCode)}
                                                                    title={allocatedDocName ? `${t("Click to Book with")} ${allocatedDocName} (${docTimingWindow})` : `${t("Click to Book in")} ${chair.title}`}
                                                                    style={allocatedDocName ? {
                                                                        borderLeft: `4px solid ${docColor}`,
                                                                        backgroundColor: `${docColor}0d`,
                                                                        borderColor: `${docColor}35`
                                                                    } : {}}
                                                                >
                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <span
                                                                                className="slot-available-dot"
                                                                                style={allocatedDocName ? { backgroundColor: docColor } : {}}
                                                                            />
                                                                            <span
                                                                                className="slot-available-text"
                                                                                style={allocatedDocName ? { color: docColor, fontWeight: 700 } : {}}
                                                                            >
                                                                                {t("Available")}
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn-quick-book"
                                                                            style={allocatedDocName ? { backgroundColor: docColor, borderColor: docColor } : {}}
                                                                        >
                                                                            <RiCheckLine /> {t("Book")}
                                                                        </button>
                                                                    </div>
                                                                    {allocatedDocName && (
                                                                        <div
                                                                            className="slot-duty-doc-badge mt-2 d-flex align-items-center justify-content-between"
                                                                            style={{
                                                                                backgroundColor: '#ffffff',
                                                                                border: `1px solid ${docColor}45`,
                                                                                color: '#0f172a'
                                                                            }}
                                                                        >
                                                                            <div className="d-flex align-items-center gap-1">
                                                                                <span
                                                                                    className="d-inline-block rounded-circle"
                                                                                    style={{ width: 8, height: 8, backgroundColor: docColor }}
                                                                                />
                                                                                <span className="font-weight-bold" style={{ color: docColor }}>{allocatedDocName}</span>
                                                                            </div>
                                                                            {docTimingWindow && (
                                                                                <span className="font-size-10 text-muted ms-1 font-monospace">{docTimingWindow}</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        }))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Container>

                {/* Modern Appointment Booking / Update Modal */}
                <Modal size="xl" isOpen={isModalOpen} toggle={toggleAppointmentModal} centered>
                    <div className="modal-header">
                        <div className="d-flex align-items-center gap-2">
                            <RiCalendarEventLine className="text-primary font-size-22" />
                            <h5 className="modal-title font-weight-bold mb-0">
                                {appointment?.id ? 'Update Operatory Appointment' : 'Book Chair Appointment'}
                            </h5>
                        </div>
                        <button type="button" className="btn-close" onClick={toggleAppointmentModal} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <div className="p-3 mb-3 rounded d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                            <div>
                                <span className="font-size-12 text-muted d-block">Slot Date & Time:</span>
                                <strong className="text-primary font-size-14">{appointment?.appointment_date || selectedDate}</strong>
                            </div>
                            <div>
                                <span className="font-size-12 text-muted d-block">{t("Operatory Chair / Cabin")}:</span>
                                <strong className="text-slate-800 font-size-14">{appointment?.chairdata?.title || t('Selected Operatory Chair')}</strong>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Select Patient *")}</label>
                                    <Select
                                        options={DDoptions?.patients || []}
                                        value={(DDoptions?.patients || []).find(v => String(v.value) === String(appointment?.patient_id)) || null}
                                        onChange={(opt) => setAppointment(p => ({ ...p, patient_id: opt?.value }))}
                                        placeholder={t("Search by Patient Name or Mobile...")}
                                        isSearchable
                                    />
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Attending Doctor / Specialist *")}</label>
                                    {isDoctor ? (
                                        <div className="form-control bg-light d-flex align-items-center gap-2" style={{ cursor: 'not-allowed' }}>
                                            <span
                                                className="d-inline-block rounded-circle flex-shrink-0"
                                                style={{ width: 10, height: 10, backgroundColor: getLoggedInDoctorOption(DDoptions?.doctors || [])?.color_code || '#F8CA15' }}
                                            />
                                            <span className="font-weight-semibold text-dark">{getLoggedInDoctorOption(DDoptions?.doctors || [])?.label || 'Doctor'}</span>
                                        </div>
                                    ) : (
                                        <Select
                                            options={DDoptions?.doctors || []}
                                            value={(DDoptions?.doctors || []).find(v =>
                                                (appointment?.doctor_code && String(v.value).toLowerCase() === String(appointment.doctor_code).toLowerCase()) ||
                                                (appointment?.doctor_code && String(v.label).toLowerCase() === String(appointment.doctor_code).toLowerCase())
                                            ) || (!appointment?.id && !appointment?.doctor_code ? getLoggedInDoctorOption(DDoptions?.doctors || []) : null)}
                                            onChange={(opt) => setAppointment(p => ({ ...p, doctor_code: opt?.value || '' }))}
                                            placeholder={t("Select Treating Doctor...")}
                                            isSearchable
                                            formatOptionLabel={(option) => (
                                                <div className="d-flex align-items-center gap-2">
                                                    <span
                                                        className="d-inline-block rounded-circle flex-shrink-0"
                                                        style={{ width: 10, height: 10, backgroundColor: option.color_code || '#F8CA15' }}
                                                    />
                                                    <span className="font-weight-semibold">{option.label}</span>
                                                    {option.email && <span className="font-size-11 text-muted ms-auto">{option.email}</span>}
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>
                            </Col>

                            <Col md={12}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Intended Treatment / Procedure")}</label>
                                    <Select
                                        options={DDoptions?.treatments || []}
                                        value={(DDoptions?.treatments || []).find(v => v.value == appointment?.treatment_code) || null}
                                        onChange={(opt) => setAppointment(p => ({ ...p, treatment_code: opt?.value }))}
                                        placeholder={t("Select Dental Treatment (Optional)...")}
                                        isSearchable
                                        isClearable
                                    />
                                </div>
                            </Col>

                            <Col md={12}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Clinical Notes / Chair Instructions")}</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        placeholder={t("Enter clinical complaints, premedication or operatory instructions...")}
                                        value={appointment?.notes || ''}
                                        onChange={(e) => setAppointment(p => ({ ...p, notes: e.target.value }))}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        {appointment?.id && (
                            <button type="button" className="btn btn-danger me-auto" onClick={() => handleCancelBooking(appointment?.id)}>
                                Cancel Appointment
                            </button>
                        )}
                        <button type="button" className="btn btn-danger" onClick={toggleAppointmentModal}>
                            Close
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleAppointmentSubmit}>
                            {appointment?.id ? 'Update Appointment' : 'Confirm & Book Appointment'}
                        </button>
                    </div>
                </Modal>

                {/* Quick Add Dental Chair / Shift Modal */}
                <Modal size="xl" isOpen={isChairModalOpen} toggle={toggleChairModal} centered>
                    <AvForm onValidSubmit={handleCreateChair}>
                        <div className="modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <RiHospitalLine className="text-primary font-size-22" />
                                <h5 className="modal-title font-weight-bold mb-0">Add Dental Chair / Operating Shift Window</h5>
                            </div>
                            <button type="button" className="btn-close" onClick={toggleChairModal} aria-label="Close" />
                        </div>
                        <div className="modal-body p-4">
                            <div className="p-3 mb-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.84rem' }}>
                                💡 <strong>Tip:</strong> If a physical operatory has morning & evening shifts (e.g. 08:00–13:00 and 15:00–20:00), use the <strong>same Cabin No</strong> for both shifts. They will automatically be consolidated into a single operatory column on the schedule grid!
                            </div>

                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="mb-2">
                                        <AvField
                                            name="title"
                                            label="Operatory Name / Shift Label *"
                                            type="text"
                                            placeholder="e.g. Operatory 2 (Morning Shift)"
                                            value={newChairData.title}
                                            onChange={(e) => setNewChairData(p => ({ ...p, title: e.target.value }))}
                                            required
                                            errorMessage="Please enter Chair / Shift Name"
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-2">
                                        <AvField
                                            name="cabin_no"
                                            label="Cabin / Operatory Room No *"
                                            type="number"
                                            placeholder="e.g. 2"
                                            value={newChairData.cabin_no}
                                            onChange={(e) => setNewChairData(p => ({ ...p, cabin_no: e.target.value }))}
                                            required
                                            errorMessage="Please enter Cabin No"
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-2">
                                        <AvField
                                            type="select"
                                            name="start_time"
                                            label="Shift Start Time *"
                                            value={newChairData.start_time}
                                            onChange={(e) => setNewChairData(p => ({ ...p, start_time: e.target.value }))}
                                            required
                                        >
                                            {timesOptions.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </AvField>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-2">
                                        <AvField
                                            type="select"
                                            name="end_time"
                                            label="Shift End Time *"
                                            value={newChairData.end_time}
                                            onChange={(e) => setNewChairData(p => ({ ...p, end_time: e.target.value }))}
                                            required
                                        >
                                            {timesOptions.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </AvField>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-2">
                                        <AvField
                                            name="intervel"
                                            label={t("Slot Interval (Mins) *")}
                                            type="number"
                                            value={newChairData.intervel}
                                            onChange={(e) => setNewChairData(p => ({ ...p, intervel: e.target.value }))}
                                            required
                                        />
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="mb-2">
                                        <label className="form-label font-weight-semibold">{t("Description / Notes")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("e.g. Morning Shift - General OPD & Scaling")}
                                            value={newChairData.description}
                                            onChange={(e) => setNewChairData(p => ({ ...p, description: e.target.value }))}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-danger" onClick={toggleChairModal}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Save Shift / Chair
                            </button>
                        </div>
                    </AvForm>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default WelcomeChairScreen;
